import React, { useCallback, useRef, useState } from "react";
import { Alert, FormHelperText, Typography, useTheme } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Input from "@components/Input/Input";
import {
  DRAWER_TEXT_MAX_CHARS,
  getDrawerTextAllowedCharactersMessage,
  isDrawerTextAllowed,
  normalizeDrawerText,
} from "@components/SideDrawer/SideDrawer.schema";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import type { GatepassError } from "@features/gatepass/components/types.ts";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { useCompleteGatepass } from "../../hooks/useCompleteGatepass";
import type { GatepassFlowData } from "../flow/ClaimGatepass";
import { GATEPASS_TASK_ROLES, isTaskForSecurityDepartment } from "../flow/utils";

const UPLOAD_KEY_REJECT = "gatepassRejectAttachment";
const service = "bff-gatepass";
const TEXTAREA_MAX_CHARS = DRAWER_TEXT_MAX_CHARS;

interface RejectGatepassDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  gatepassFlowData?: GatepassFlowData;
  setFakeStatus?: (status: string | null) => void;
}

const RejectGatepassDrawer: React.FC<RejectGatepassDrawerProps> = ({
  isOpen,
  onClose,
  gatepassFlowData,
  setFakeStatus,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);
  const { notify } = useNotification();
  const taskRole = gatepassFlowData?.task?.role;
  const status = gatepassFlowData?.status ?? "";
  const isFott = taskRole === GATEPASS_TASK_ROLES.FOTT;
  const isSponsorCompany = taskRole === GATEPASS_TASK_ROLES.SPONSOR_COMPANY;
  const isSecurityDepartment = isTaskForSecurityDepartment(status, taskRole);

  const [rejectReason, setRejectReason] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState<string | undefined>();
  const [errors, setErrors] = useState<{ rejectReason?: string; files?: string }>({});
  const [error, setError] = useState<null | string>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<{
    status: "REJECTED";
    reason: string;
    attachments?: Array<{
      originalFileName: string;
      url: string;
      type: string;
    }>;
  } | null>(null);

  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const rejectAttachmentFiles = useUploadStore(
    (state) => state.filesByKey[UPLOAD_KEY_REJECT] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const uploadSignatureRef = useRef<string | null>(null);

  const setRejectAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(UPLOAD_KEY_REJECT);
        return;
      }
      setFilesForKey(UPLOAD_KEY_REJECT, files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "RejectGatepass",
          action: "upload_gatepass_document",
          entityType: "gatepass_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };
          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });
          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          return { documentUrl, documentType };
        },
      ),
    onError: (error: Error) => setError(error.message),
  });

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.RefObject<string | null>,
    ) => {
      if (!handler || !(entry instanceof File)) return;
      const signature = getFileSignature(entry);
      if (!signature || tracker.current === signature) return;
      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const handleDocumentUpload = useCallback((file: File) => {
    documentUploadMutation.mutate(
      { file, documentType: "GATEPASS_REJECT_ATTACHMENT" },
      {
        onSuccess: ({ documentUrl }: { documentUrl: string }) => {
          setError(null);
          setAttachmentUrl(documentUrl);
        },
      },
    );
  }, []);

  const handleFileUpload = useCallback(
    (newFiles: File[]) => {
      if (newFiles.length > 1) {
        setErrors((prev) => ({ ...prev, files: t("upload.one-document") }));
        return;
      }
      const file = newFiles[0];
      if (!file) return;
      setErrors((prev) => ({ ...prev, files: undefined }));
      setRejectAttachmentFiles([file]);
      triggerUpload(file, handleDocumentUpload, uploadSignatureRef);
    },
    [handleDocumentUpload, setRejectAttachmentFiles, t, triggerUpload],
  );

  const validateRejectReason = useCallback(
    (value: string) => {
      const trimmed = normalizeDrawerText(value);
      const label = t("gatepass.reject-reason", { defaultValue: "Reject reason" });

      if (!trimmed) {
        return t("form-validation.required", { label });
      }

      if (trimmed.length > TEXTAREA_MAX_CHARS) {
        return t("form-validation.max-length", {
          label,
          max: TEXTAREA_MAX_CHARS,
        });
      }

      if (!isDrawerTextAllowed(trimmed)) {
        return getDrawerTextAllowedCharactersMessage(t);
      }

      return undefined;
    },
    [t],
  );

  const resetFormValues = useCallback(() => {
    setRejectReason("");
    setAttachmentUrl(undefined);
    setErrors({});
    setRejectAttachmentFiles([]);
    setPendingPayload(null);
    uploadSignatureRef.current = null;
  }, [setRejectAttachmentFiles]);

  const handleSuccess = useCallback(() => {
    setFakeStatus?.(null);
    onClose("rejectDrawer");
    resetFormValues();
    setShowConfirmDialog(false);
    notify({
      title: t("gatepass.reject-success", { defaultValue: "Gatepass rejected" }),
      type: "success",
    });
  }, [notify, onClose, resetFormValues, setFakeStatus, t]);

  const handleError = useCallback(
    (err: GatepassError) => {
      setFakeStatus?.(null);
      onClose("rejectDrawer");
      resetFormValues();
      setShowConfirmDialog(false);
      notify({
        title: t("common.error"),
        description:
          err?.response?.data?.detail ?? err?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    [notify, onClose, resetFormValues, setFakeStatus, t],
  );

  const { completeGatepass, isPending } = useCompleteGatepass({
    handleSuccess,
    handleError,
  });

  const handleRejectSubmit = () => {
    if (documentUploadMutation.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const trimmedReason = normalizeDrawerText(rejectReason);
    setRejectReason(trimmedReason);

    const reasonError = validateRejectReason(trimmedReason);
    if (reasonError) {
      setErrors((prev) => ({ ...prev, rejectReason: reasonError }));
      return;
    }
    const payload: {
      status: "REJECTED";
      reason: string;
      attachments?: Array<{
        originalFileName: string;
        url: string;
        type: string;
      }>;
    } = { status: "REJECTED", reason: trimmedReason };
    if (attachmentUrl) {
      const file = rejectAttachmentFiles[0];
      const originalFileName =
        file instanceof File ? file.name : (file as { name?: string })?.name || "attachment";
      payload.attachments = [
        {
          originalFileName,
          url: attachmentUrl,
          type: "REJECT_ATTACHMENTS",
        },
      ];
    }
    setPendingPayload(payload);
    setShowConfirmDialog(true);
  };

  const handleConfirmDialogConfirm = () => {
    if (!gatepassFlowData?.id || !pendingPayload) return;
    completeGatepass({
      id: gatepassFlowData.id,
      service,
      payload: pendingPayload,
    });
  };

  const handleConfirmDialogClose = () => {
    setShowConfirmDialog(false);
    setPendingPayload(null);
  };

  const handleCloseDrawer = () => {
    onClose("rejectDrawer");
    resetFormValues();
  };

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const inputSlotProps = {
    root: {
      className: "rounded-lg text-sm px-4 py-2 border",
      style: {
        borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
        color: theme.palette.text.primary,
      },
    },
    htmlInput: { maxLength: TEXTAREA_MAX_CHARS },
  };

  const sxProps = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      borderColor: theme.palette.strokeColor?.primary ?? "#BBC1C5",
      padding: "12px",
      fontSize: theme.typography.body1.fontSize,
      color: theme.palette.text.primary,
    },
    "& textarea": { padding: 0 },
  };

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  if (!gatepassFlowData || (!isFott && !isSponsorCompany && !isSecurityDepartment)) {
    return null;
  }

  // Side drawer with reject reason, attachments and confirmation
  return (
    <div data-testid="reject-gatepass-drawer-form" className="max-h-screen overflow-y-auto">
      <SideDrawer
        isOpen={isOpen}
        title={t("gatepass.reject-drawer-title", { defaultValue: "Reject Gatepass" })}
        onClose={handleCloseDrawer}
        submitLabel={t("gatepass.reject-button", { defaultValue: "Reject" })}
        onSubmit={handleRejectSubmit}
        isSubmitting={isPending}
      >
        <div data-testid="reject-gatepass-drawer-content" className="flex-1 max-w-sm">
          <div className="px-6">
            <Typography
              variant="body1"
              data-testid="reject-gatepass-form-title"
              sx={{ color: "text.primary", pb: 4 }}
            >
              {t("gatepass.reject-drawer-message", {
                defaultValue: "You are about to reject Gatepass",
              })}
              <br />
              <strong>#{gatepassFlowData?.serialNumber ?? gatepassFlowData?.id ?? ""}</strong>
            </Typography>
            <div className="col-span-full mb-0 flex flex-col gap-2 mt-6">
              <CustomFormLabel>
                {t("gatepass.reject-reason", { defaultValue: "Reject reason" })}
              </CustomFormLabel>
              <Input
                placeholder={t("gatepass.reject-reason-placeholder", {
                  defaultValue: "Enter reject reason",
                })}
                multiline
                minRows={4}
                fullWidth
                variant="outlined"
                value={rejectReason}
                onKeyDown={(e) => {
                  if (!isPrintableKey(e)) return;
                  if (rejectReason.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      rejectReason: t("form-validation.max-length", {
                        label: t("gatepass.reject-reason"),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  setRejectReason(value);
                  setErrors((prev) => ({ ...prev, rejectReason: validateRejectReason(value) }));
                }}
                onBlur={(e) => {
                  const trimmed = normalizeDrawerText(e.target.value);
                  if (trimmed !== rejectReason) {
                    setRejectReason(trimmed);
                  }
                  setErrors((prev) => ({
                    ...prev,
                    rejectReason: validateRejectReason(trimmed),
                  }));
                }}
                slotProps={inputSlotProps}
                sx={sxProps}
                testId="reject-gatepass-reason-input"
                error={!!errors.rejectReason}
              />
              {errors.rejectReason && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {errors.rejectReason}
                </p>
              )}
              <FormHelperText sx={charCounterSx}>
                {rejectReason.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
            </div>
            <div className="col-span-full mt-6">
              <div className={errors.files ? "border border-red-500 rounded-md p-1" : ""}>
                <DocumentUpload
                  label={t("gatepass.attachments", { defaultValue: "Attachments" })}
                  testId="reject-gatepass-attachment-upload"
                  name={UPLOAD_KEY_REJECT}
                  isSideDrawer
                  maxFiles={1}
                  allowedExtensions={allowedExtensions}
                  onUpload={handleFileUpload}
                  conditions={FILE_UPLOAD_PROFILES.sideDrawerApprovalFile}
                  files={rejectAttachmentFiles}
                  setFiles={setRejectAttachmentFiles}
                  uploadDescription={t("upload.dragOrDropDescription")}
                  disabled={documentUploadMutation.isPending}
                />
              </div>
              {errors.files && (
                <p style={styles.errorText} className="text-sm mt-1">
                  {errors.files}
                </p>
              )}
              {rejectAttachmentFiles[0] && !errors.files && (
                <div className="flex-1 mt-4">
                  <SingleFile
                    file={rejectAttachmentFiles[0]}
                    fieldName={UPLOAD_KEY_REJECT}
                    setFiles={() => {
                      setErrors((prev) => ({ ...prev, files: undefined }));
                      setRejectAttachmentFiles([]);
                      setAttachmentUrl(undefined);
                      uploadSignatureRef.current = null;
                    }}
                    isLoading={documentUploadMutation.isPending}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
      <GenericDialog
        open={showConfirmDialog}
        title={t("gatepass.reject-confirm-title", { defaultValue: "Confirm reject" })}
        elementContent={
          <>
            {t("gatepass.reject-confirm-message", {
              defaultValue: "You are about to reject Gatepass",
            })}{" "}
            <strong>#{gatepassFlowData?.serialNumber ?? gatepassFlowData?.id ?? ""}</strong>.
            <br />
            {t("gatepass.reject-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm")}
        handleCloseDialog={handleConfirmDialogClose}
        confirmAction={handleConfirmDialogConfirm}
        testId="reject-gatepass-confirm-dialog"
        isInsideDrawer
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default RejectGatepassDrawer;
